import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useLatestRef } from '@hooks/useLatestRef';
import { useReportNotificationClick } from '@hooks/useReportNotificationClick';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { resolveNotificationTarget } from '@lib/notifications/resolveNotificationTarget';
import { NREF_PARAM, sanitizeRefs, type PushNotificationRefs } from '@lib/pwa/push-navigation';
import { NOTIFICATION_TAP_FALLBACK } from '@utils/safePath';
import { logger } from '@utils/logger';

/**
 * Cold-start leg of the N1 web-push destination contract (task W3).
 *
 * When a notification is tapped with no app window open, the service worker
 * opens `/notifications?nref=<JSON refs>` (see buildNrefUrlFromRefs in
 * @lib/pwa/push-navigation) instead of resolving the destination itself.
 * This hook - mounted once in RootLayout via NrefBootResolver, beside the
 * push initializers - consumes the param in-page:
 *
 * - valid refs: resolveNotificationTarget picks the destination, then a
 *   replace-navigation strips the nref URL from history;
 * - malformed refs (bad JSON / non-object): strip ONLY the nref param, keep
 *   any other search params, stay put;
 * - unknown type / unbuildable refs: the resolver falls back to the guest-safe
 *   home (NOTIFICATION_TAP_FALLBACK - a cold-start viewer may be signed out and
 *   /notifications is ProtectedRoute-wrapped, rendering null when signed out;
 *   there is no legacy url on this leg, so the fallback IS the recovery path)
 *   and this hook - owning the known=false side effects per the resolver
 *   contract - fires logger.error plus notification_unknown_type with surface
 *   'nref_boot'.
 *
 * Auth interplay: /notifications is a protected route that is NOT excluded
 * by shouldStoreRedirectPath, so a signed-out boot preserves path + search
 * through the login round-trip. The post-login redirect restores the nref
 * URL with a CLIENT-SIDE navigation, which is why the effect keys on the
 * search string rather than running strictly once on mount; the
 * param-existence guard keeps every ordinary location change a no-op, and
 * the consumed ref makes the resolution run at most once per app boot
 * (StrictMode re-runs included).
 */
export function useNrefBootResolve(): void {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const { trackEvent } = useAmplitudeTracking();
  const reportClick = useReportNotificationClick();

  // Latest-value refs so the resolution effect depends only on the location
  // primitives (mirrors useSwNotificationNavigate / usePushNotifications).
  const navigateRef = useLatestRef(navigate);
  const trackEventRef = useLatestRef(trackEvent);
  const reportClickRef = useLatestRef(reportClick);

  // Run-once guard: a boot consumes at most one nref.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    const params = new URLSearchParams(search);
    const raw = params.get(NREF_PARAM);
    if (raw === null) return; // only act when the param exists
    consumedRef.current = true;

    let refs: PushNotificationRefs | null = null;
    try {
      refs = sanitizeRefs(JSON.parse(raw));
    } catch {
      refs = null;
    }

    if (!refs) {
      // Malformed payload: strip the nref param (keeping any other search
      // params and the hash) and stay where we are.
      logger.warn('nref boot param is not valid refs JSON; stripping it');
      params.delete(NREF_PARAM);
      const remaining = params.toString();
      navigateRef.current(`${pathname}${remaining ? `?${remaining}` : ''}${hash}`, {
        replace: true,
      });
      return;
    }

    // N3 "honest bell": the cold-start nref now carries notification_id (the
    // pruner always keeps it), so report the tap here (fire-and-forget) before
    // resolving/navigating. Guard the id-absent case (legacy / pre-deploy
    // pushes, whose nref has no notification_id).
    const notificationId = refs.notification_id;
    if (notificationId) reportClickRef.current(notificationId);

    // Cold-start push: the viewer may be signed out, so an unresolved target
    // must land on the guest-safe home, not the ProtectedRoute-wrapped
    // /notifications (which renders null when signed out). No legacy url rides
    // this leg (the SW packs only refs into ?nref=), so the fallback IS the
    // recovery path.
    const target = resolveNotificationTarget(refs.type, refs, {
      unknownFallback: NOTIFICATION_TAP_FALLBACK,
    });
    if (!target.known) {
      logger.error(
        `nref boot destination unresolved (type=${refs.type ?? 'missing'}); falling back to ${target.path}`,
      );
      trackEventRef.current(ANALYTICS_EVENTS.notification_unknown_type, {
        type: refs.type ?? null,
        surface: 'nref_boot',
      });
    }
    navigateRef.current(target.path, { replace: true });
    // navigateRef/trackEventRef/reportClickRef are stable useLatestRef handles -
    // listed to satisfy exhaustive-deps; resolution still keys on the location primitives.
  }, [search, pathname, hash, navigateRef, trackEventRef, reportClickRef]);
}
